<template>
  <div class="terms-of-use__wrapper">
    <div class="terms-of-use">
      <div class="terms-of-use__head-wrapper">
        <div class="terms-of-use__head">
          <div class="bold-600 fs-36 mb-3">
            Cookie Policy
          </div>
          <div class="bold fs-18">
            Effective Date: May 08, 2026
          </div>
        </div>
      </div>

      <div class="terms-of-use__content-wrapper">
        <div class="terms-of-use__content">
          <p>
            Our Website uses cookies. This Cookie Policy explains how we use
            cookies when you visit our Website at
            <a
              :href="websiteUrl"
              rel="noopener noreferrer"
              target="_blank"
            >https://satoshieye.com</a>. It also informs you about your rights to control the scope of our
            use of Cookie.
          </p>

          <div class="bold-600 fs-16 mb-3">
            What are cookies?
          </div>
          <p>
            Cookies are small text files placed on your device by the website
            you visit. They serve various purposes, such as enhancing website
            performance, understanding visitor usage, and ensuring security.
            "First-party cookies" are set directly by our Website, while
            "third-party cookies" are set by entities other than our Website and
            may enable features like advertising, interactive content, and
            analytics. These third-party entities might recognize your computer
            both on our Website and elsewhere on the internet.
          </p>

          <div class="bold-600 fs-16 mb-3">
            Types of Cookies We Use.
          </div>
          <ul>
            <li>
              Essential or Strictly Necessary Cookies: These are vital for
              navigating our Website and using its features. Without these
              cookies, certain services cannot be provided.
            </li>
            <li>
              Performance Cookies: These collect information about how you
              interact with our Website, helping us optimize and ease
              navigation.
            </li>
            <li>
              Analytical or Functional Cookies: These allow our Website to
              remember your choices and provide a more personalized experience.
            </li>
            <li>
              Third-Party Cookies: Placed by entities other than us, these
              cookies enhance our services or help deliver more relevant
              advertising, subject to their own privacy policies.
            </li>
          </ul>

          <div class="bold-600 fs-16 mb-3">
            Legal Basis for Using Cookies
          </div>
          <p>
            We rely on different legal bases depending on the type of cookies
            used:
          </p>
          <ul>
            <li>
              Strictly necessary cookies are processed as required to provide
              the services you request (e.g. account access, payment processing,
              and security) and do not require your consent.
            </li>
            <li>
              Analytics, performance, and functional cookies are used based on
              your consent, where required by applicable law.
            </li>
            <li>
              Marketing or advertising cookies (if used) are also based on your
              consent. You may withdraw your consent at any time through the
              cookie consent manager available on our Website. Marketing and
              advertising cookies may be used to understand your interactions
              with our Website, build audience insights, and help deliver more
              relevant advertising or communications.
            </li>
          </ul>
          <p>
            Such cookies may involve profiling or similar automated processing.
            Where required by applicable law, these cookies will be used only
            with your consent.
          </p>

          <div class="bold-600 fs-16 mb-3">
            Marketing Cookies
          </div>
          <p>
            Marketing cookies and similar technologies may be used to help us
            deliver more relevant advertising and communications, measure
            campaign effectiveness, build marketing audiences, limit repeated
            advertisements, and personalize content based on your interactions
            with our Website.
          </p>
          <p>
            Depending on the tools used, these technologies may recognize your
            browser or device over time and may support advertising on
            third-party platforms after you visit our Website.
          </p>
          <p>
            Where required by applicable law, marketing cookies are placed only
            with your prior consent. You may withdraw or manage your preferences
            at any time through our cookie settings.
          </p>

          <div class="bold-600 fs-16 mb-3">
            Cookie and Technology Details
          </div>
          <p>
            Below is an overview of the main cookies and similar technologies
            used on our Website. The specific cookies may vary depending on how
            you interact with our services.
          </p>
          <o-table
            class="classic-table cookie-policy-table mb-3"
            :data="cookieRows"
            hoverable
            :mobile-cards="false"
            scrollable
          >
            <o-table-column
              field="name"
              label="Name"
            >
              <template #default="{ row }">
                <div class="white-space-nowrap">
                  {{ row.name }}
                </div>
              </template>
            </o-table-column>
            <o-table-column
              field="provider"
              label="Provider"
            >
              <template #default="{ row }">
                <div class="white-space-nowrap">
                  {{ row.provider }}
                </div>
              </template>
            </o-table-column>
            <o-table-column
              field="purpose"
              label="Purpose"
            >
              <template #default="{ row }">
                <div class="min-w-250 white-space-normal">
                  {{ row.purpose }}
                </div>
              </template>
            </o-table-column>
            <o-table-column
              field="type"
              label="Type"
            >
              <template #default="{ row }">
                <div class="white-space-nowrap">
                  {{ row.type }}
                </div>
              </template>
            </o-table-column>
            <o-table-column
              field="duration"
              label="Duration"
            >
              <template #default="{ row }">
                <div class="white-space-nowrap">
                  {{ row.duration }}
                </div>
              </template>
            </o-table-column>
          </o-table>
          <p>
            We may also use similar technologies provided by hosting providers,
            authentication services, and fraud prevention tools to ensure the
            secure operation of the Website.
          </p>

          <div class="bold-600 fs-16 mb-3">
            Retention of Cookies
          </div>
          <p>
            Cookies may be stored on your device for different periods of time:
          </p>
          <ul>
            <li>
              Session cookies are deleted automatically when you close your
              browser.
            </li>
            <li>
              Persistent cookies remain on your device for a defined period or
              until you delete them manually.
            </li>
          </ul>
          <p>The duration of each cookie is indicated in the table above.</p>

          <div class="bold-600 fs-16 mb-3">
            Your choices regarding cookies:
          </div>
          <p>
            You have the right to decide whether to accept or reject
            non-essential cookies.
          </p>
          <p>
            When you first visit our Website, you will be presented with a
            cookie consent banner that allows you to:
          </p>
          <ul>
            <li>accept all cookies;</li>
            <li>reject all non-essential cookies; or</li>
            <li>manage your preferences by category.</li>
          </ul>
          <p>
            Non-essential cookies (including analytics and personalization
            cookies) are only set after you have provided your consent, where
            required by applicable law.
          </p>
          <p>
            You can withdraw or modify your consent at any time via the cookie
            consent manager available on our Website.
          </p>
          <p>
            You may also configure your browser settings to block or delete
            cookies. Please note that disabling certain cookies may affect the
            functionality of the Website.
          </p>

          <div class="bold-600 fs-16 mb-3">
            Strictly Necessary Payment and Security Technologies
          </div>
          <p>
            We use cookies and similar technologies that are strictly necessary
            to provide secure account login, checkout functionality, payment
            processing, subscription administration, fraud prevention, and
            platform security. These technologies may be set by SatoshiEye or by
            third-party service providers acting on our behalf, including
            payment processors, identity and authentication providers, hosting
            providers, and fraud-prevention vendors.
          </p>
          <p>These technologies may, for example, be used to:</p>
          <ul>
            <li>maintain your checkout session;</li>
            <li>remember the plan selected during the payment flow;</li>
            <li>authenticate you while you access your account;</li>
            <li>prevent duplicate charges or repeated payment submissions;</li>
            <li>detect suspicious activity, bots, or fraud; and</li>
            <li>
              support the secure processing of renewals, cancellations, refunds,
              and payment failures.
            </li>
          </ul>
          <p>
            We use Stripe checkout and payment tools to process payments and
            subscriptions. Stripe may use cookies and similar technologies, as
            well as IP addresses and interaction data relating to the checkout
            flow, to provide payment services, prevent fraud, and improve its
            services.
          </p>
          <p>
            As part of providing its services, Stripe may process personal data
            in multiple jurisdictions.
          </p>
          <p>
            Where permitted by applicable law, strictly necessary technologies
            are used without optional consent because they are required to
            provide the service you request. We do not use strictly necessary
            payment or security technologies for advertising purposes.
          </p>
          <p>
            If we use analytics, personalization, or marketing technologies in
            connection with billing pages or account pages, we will request
            consent where required by applicable law and describe those
            technologies separately in this Cookie Policy.
          </p>

          <div class="bold-600 fs-16 mb-3">
            International Data Processing
          </div>
          <p>
            We operate on a global basis and may use service providers that
            process data in multiple jurisdictions.
          </p>
          <p>
            As a result, personal data collected through cookies and similar
            technologies may be processed in countries other than the one in
            which you are located.
          </p>
          <p>
            Where required by applicable law, such processing is carried out
            subject to appropriate safeguards or other lawful transfer
            mechanisms implemented by the relevant provider or by us, as
            applicable.
          </p>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'CookiePolicy',
  data() {
    return {
      cookieRows: [
        {
          name: '_stripe_sid',
          provider: 'Stripe',
          purpose: 'Maintains secure payment session and prevents fraud',
          type: 'Strictly necessary',
          duration: '1 day / Session',
        },
        {
          name: '_stripe_mid',
          provider: 'Stripe',
          purpose: 'Fraud detection and payment security',
          type: 'Strictly necessary',
          duration: '1 year',
        },
        {
          name: '_ga',
          provider: 'Google Analytics 4',
          purpose: 'Distinguishes users and enables analytics on website usage',
          type: 'Analytics',
          duration: '2 years',
        },
        {
          name: '_ga_<container-id>',
          provider: 'Google Analytics 4',
          purpose: 'Maintains session state and tracks behavior',
          type: 'Analytics',
          duration: '2 years',
        },
        {
          name: '_hjSessionUser_<site-id>',
          provider: 'Hotjar',
          purpose: 'Stores unique user ID across visits',
          type: 'Analytics',
          duration: '1 year',
        },
        {
          name: '_hjSession_<site-id>',
          provider: 'Hotjar',
          purpose: 'Holds session data for usability analysis',
          type: 'Analytics',
          duration: '30 minutes',
        },
        {
          name: '_cs_id',
          provider: 'ContentSquare',
          purpose: 'Tracks interactions to improve UX',
          type: 'Analytics',
          duration: '13 months',
        },
        {
          name: '_cs_s',
          provider: 'ContentSquare',
          purpose: 'Stores session data for analytics',
          type: 'Analytics',
          duration: 'Session',
        },
        {
          name: 'personalization_id',
          provider: 'Twitter / X',
          purpose: 'Tracks activity for personalized ads',
          type: 'Marketing',
          duration: '2 years',
        },
        {
          name: 'li_fat_id',
          provider: 'LinkedIn',
          purpose: 'Conversion tracking and retargeting',
          type: 'Marketing',
          duration: '30 days',
        },
        {
          name: 'li_sugr',
          provider: 'LinkedIn',
          purpose: 'Browser data for analytics',
          type: 'Marketing',
          duration: '90 days',
        },
        {
          name: '_twitter_sess',
          provider: 'X (Twitter)',
          purpose: 'Embedded content + ad tracking',
          type: 'Marketing',
          duration: 'Session',
        },
        {
          name: 'muc_ads',
          provider: 'X (Twitter)',
          purpose: 'Measures ad engagement',
          type: 'Marketing',
          duration: '2 years',
        },
        {
          name: '_telegram_session',
          provider: 'Telegram',
          purpose: 'Enables widget functionality',
          type: 'Functional',
          duration: 'Session',
        },
        {
          name: '_mc_id',
          provider: 'Mailchimp',
          purpose: 'Tracks email campaign performance',
          type: 'Marketing',
          duration: '1 year',
        },
        {
          name: '_mc_s',
          provider: 'Mailchimp',
          purpose: 'Maintains form session state',
          type: 'Functional',
          duration: 'Session',
        },
      ],
    }
  },
  computed: {
    websiteUrl() {
      return 'https://satoshieye.com'
    },
  },
}
</script>

